import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ListChecks, Upload } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const MAX = 50;
const BADGE = {
    ok: ["Encontrado na ANM", "bg-emerald-50 text-emerald-700 border-emerald-200"],
    cadastrado: ["Cadastrado", "bg-emerald-600 text-white border-emerald-600"],
    nao_encontrado: ["Não localizado", "bg-amber-50 text-amber-700 border-amber-200"],
    duplicado: ["Já cadastrado", "bg-slate-100 text-slate-600 border-slate-200"],
    invalido: ["Formato inválido", "bg-red-50 text-red-700 border-red-200"],
    limite: ["Fora do limite do plano", "bg-orange-50 text-orange-700 border-orange-200"],
    erro: ["Erro", "bg-red-50 text-red-700 border-red-200"],
};

const countTokens = (t) => new Set(t.split(/[\n,;\t]+/).map((s) => s.replace(/\s+/g, "")).filter(Boolean)).size;

function ResultRow({ it }) {
    const [label, cls] = BADGE[it.status] || [it.status, "bg-slate-100"];
    const f = it.fields;
    return (
        <div className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-0" data-testid={`batch-row-${it.input}`}>
            <div className="min-w-0">
                <div className="font-mono text-sm font-bold text-petroleum">{it.input}</div>
                {it.deadlines_created > 0 && <div className="text-[11px] text-emerald-700" data-testid={`batch-deadlines-${it.input}`}>{it.deadlines_created} prazo(s) criado(s)</div>}
                <div className="text-xs text-slate-500 truncate">
                    {f ? `${f.holder || "—"} · ${f.substance || "—"} · ${f.municipality || "—"}/${f.uf || ""}` : it.detail}
                </div>
            </div>
            <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full border ${cls}`} data-testid={`batch-status-${it.input}`}>{label}</span>
        </div>
    );
}

export function BatchImportDialog({ open, onOpenChange, onImported }) {
    const navigate = useNavigate();
    const [text, setText] = useState("");
    const [items, setItems] = useState(null);
    const [done, setDone] = useState(false);
    const [busy, setBusy] = useState(false);
    const total = countTokens(text);
    const okCount = items?.filter((i) => i.status === "ok").length || 0;

    const reset = () => { setText(""); setItems(null); setDone(false); };
    const close = (v) => { if (!v) reset(); onOpenChange(v); };

    const call = async (path) => {
        setBusy(true);
        try {
            const r = await api.post(path, { text });
            if (r.data.status === "baixando_base") { toast.info("Preparando a base da ANM", { description: r.data.detail }); return null; }
            setItems(r.data.items);
            return r.data;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Não foi possível processar a lista.");
            return null;
        } finally { setBusy(false); }
    };

    const preview = () => { setDone(false); call("/processes/batch/preview"); };
    const importar = async () => {
        const d = await call("/processes/batch/import");
        if (!d) return;
        setDone(true);
        const n = d.items.filter((i) => i.status === "cadastrado").length;
        const dl = d.items.reduce((s, i) => s + (i.deadlines_created || 0), 0);
        toast.success(`${n} processo(s) cadastrado(s).`, dl ? { description: `${dl} prazo(s) criado(s) a partir das datas oficiais da ANM.` } : undefined);
        if (d.limit) {
            toast.warning(d.limit.message || "Limite do plano atingido.", {
                description: "Alguns processos ficaram de fora.",
                action: { label: "Meu Plano", onClick: () => navigate("/plano") },
            });
        }
        onImported();
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="max-w-2xl" data-testid="batch-import-dialog">
                <DialogHeader>
                    <DialogTitle className="font-heading">Importar processos em lote</DialogTitle>
                    <DialogDescription>Cole os números (um por linha ou separados por vírgula), no formato 844030/2021. Até {MAX} por vez. Os dados são preenchidos pela base oficial da ANM.</DialogDescription>
                </DialogHeader>
                {!items ? (
                    <div>
                        <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} data-testid="batch-textarea"
                            placeholder={"844030/2021\n850.725/2020\n831234/2019"} className="font-mono text-sm" />
                        <div className={`text-xs mt-1 ${total > MAX ? "text-red-600" : "text-slate-500"}`} data-testid="batch-count">{total} de {MAX} processos</div>
                    </div>
                ) : (
                    <div className="max-h-[50vh] overflow-y-auto pr-1" data-testid="batch-results">
                        {items.map((it) => <ResultRow key={it.input} it={it} />)}
                    </div>
                )}
                <DialogFooter className="gap-2">
                    {items && !done && <button onClick={() => setItems(null)} className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm font-medium" data-testid="batch-back-btn">Editar lista</button>}
                    {!items && (
                        <button onClick={preview} disabled={busy || !total || total > MAX} data-testid="batch-preview-btn"
                            className="bg-petroleum hover:bg-petroleumHover text-white rounded-sm px-6 py-2 font-semibold flex items-center gap-2 transition-colors disabled:opacity-60">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListChecks className="w-4 h-4" />} Verificar na ANM
                        </button>
                    )}
                    {items && !done && (
                        <button onClick={importar} disabled={busy || !okCount} data-testid="batch-import-btn"
                            className="bg-mineral hover:bg-mineralHover text-white rounded-sm px-6 py-2 font-semibold flex items-center gap-2 transition-colors disabled:opacity-60">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Cadastrar {okCount} processo(s)
                        </button>
                    )}
                    {done && <button onClick={() => close(false)} data-testid="batch-close-btn" className="bg-petroleum text-white rounded-sm px-6 py-2 font-semibold">Concluir</button>}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
